import { Box, CircularProgress } from "@mui/material";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import classNames from "classnames";
import smoothScroll from "../../utils/animateScroll";
import { useInputMode } from "../../utils/hooks/useInputMode";

const FocusableSelectItem = ({
  value,
  label,
  onBlur,
  onEnterPress,
  onFocus,
  setExpanded,
  focusable,
  shouldFocus = false,
}) => {
  const _onEnterPress = () => {
    onEnterPress(value);
    if (typeof onBlur === "function") onBlur();
    if (typeof setExpanded === "function") setExpanded();
  };

  const { ref, focused, focusSelf } = useFocusable({
    onEnterPress: _onEnterPress,
    onFocus,
    focusable,
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  useEffect(() => {
    if (focusable && shouldFocus) {
      focusSelf();
    }
  }, [shouldFocus, focusable]);

  return (
    <Box
      ref={ref}
      onMouseEnter={handleMouseEnter}
      sx={{
        backgroundColor: focused ? "#630000ff" : "#393939",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      height={72}
      className="cursor-pointer"
      onClick={_onEnterPress}
    >
      {label}
    </Box>
  );
};

const FocusableSelectBoundary = ({
  onBlur,
  value,
  options,
  onChange,
  label,
  className,
  instanceFocusKey,
  isLoading,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [softFocus, setSoftFocus] = useState(false);
  const [selected, setSelected] = useState();
  const childRef = useRef(false);
  const innerRef = useRef();

  const { ref, focusKey, hasFocusedChild, focusSelf } = useFocusable({
    trackChildren: true,
    focusKey: instanceFocusKey,
    onFocus: () => setSoftFocus(true),
    onBlur: () => {
      setSoftFocus(false);
      setTimeout(() => {
        if (!childRef.current) {
          setSoftFocus(false);
          setExpanded(false);
          setSelected(null);
        }
      }, 250);
    },
    onEnterPress: () => setExpanded(true),
  });
  const { setMouseFocus, isMouseFocus } = useInputMode();
  const handleMouseEnterBoundary = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  useEffect(() => {
    childRef.current = hasFocusedChild;
  }, [hasFocusedChild]);

  const onFocus = useCallback(
    (props) => {
      if (isMouseFocus) return;
      const { y, height } = props;

      const targetScroll = y > height - 200 ? y - 200 : 0;

      smoothScroll(innerRef, targetScroll + 100, 500);
    },
    [innerRef, isMouseFocus]
  );

  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        ref={ref}
        onMouseEnter={handleMouseEnterBoundary}
        onClick={() => {
          setExpanded(!expanded)
          if (expanded) {
            setSelected(null);
          }
        }}
        className={classNames("cursor-pointer", className && className)}
        position={"relative"}
        sx={{
          color: "white",
          letterSpacing: 0,
          fontSize: "30px",
          fontWeight: "500",
          width: "100%",
          textAlign: "center",
          "& .MuiSelect-icon": { color: "rgba(217,217,217,1)!important" },
          border: softFocus
            ? "inset 0 0 0 4px #8a1108"
            : "inset 0 0 0 4px #535353ff",
          borderRadius: "20px",
          height: expanded ? 365 : 72,
          transition: "height 0.2s",
          backgroundColor: softFocus ? "#8a1108" : " #262626",
          my: 2,
          zIndex:99
        }}
      >
        <Box
          height={72}
          width={"100%"}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          sx={{
            backgroundColor: softFocus ? "#8a1108" : " #262626",
            borderRadius: expanded ? "16px 16px 0 0" : "16px",
          }}
          position={"relative"}
          zIndex={10}
        >
          <ArrowDropDownIcon
            fontSize="large"
            sx={{
              position: "absolute",
              right: 15,
              top: 15,
              transform: expanded ? "rotate(180deg)" : "rotate(0)",
              transition: "transform .2s",
            }}
          />
          {options?.filter((x) => x.value === value)?.[0]?.label || label}
        </Box>
        <Box sx={{
          position: "absolute",
          top: 280,
          left: 0,
          width: 40,
          height: 40,
          opacity: !expanded ? 0 : 1,
          transform: '0.3s ease all',
          zIndex: 100,
        }}>
          {selected > 2 && <ArrowDropDownIcon
            fontSize="large"
            sx={{
              transform: 'rotate(180deg)',
              marginBottom: '-40px'
            }}
          />}

          {selected < options.length - 3 && <ArrowDropDownIcon
            fontSize="large"
            sx={{
              marginTop: selected > 2 ? 0 : "38px"
            }}
          />}
        </Box>
        <Box
          position={"absolute"}
          top={expanded ? 72 : 0}
          right={4}
          left={4}
          height={
            expanded ? (options?.length < 5 ? options.length * 72 : 288) : 0
          }
          ref={innerRef}
          sx={{
            transition: "all ease .2s",
            opacity: expanded ? 1 : 0,
            borderRadius: "0 0 16px 16px",
            backgroundColor: softFocus ? "#be0101a9" : " #262626",
            overflowY: "auto",
          }}
          zIndex={5}
        >
          <Box
            height={
              expanded ? (options?.length < 5 ? options.length * 72 : 288) : 0
            }
            sx={{
              transition: "transform ease .3s",
            }}
          >
            {isLoading ? (
              <CircularProgress color="error" className="mx-auto my-2" />
            ) : (
              options.map((item, index) => (
                <FocusableSelectItem
                  key={item.value + index}
                  label={item.label}
                  value={item.value}
                  onFocus={(e) => {
                    onFocus?.(e);
                    setSelected(index);
                  }}
                  onEnterPress={onChange}
                  shouldFocus={expanded && index === 0}
                  setExpanded={() => {
                    setSoftFocus(false);
                    setExpanded(false);
                  }}
                  focusable={expanded}
                  onBlur={onBlur}
                />
              ))
            )}

          </Box>
        </Box>
      </Box>
    </FocusContext.Provider >
  );
};

export default FocusableSelectBoundary;
